import type { CrmKpi } from '@portal/contracts';
import { onLinkClick } from '../../router.js';
import {
  AMPEL_LABEL, aktiveFilter, filterLabel, istAktiv, kacheln, laufStatusLabel, listenPfad, modusLabel, ohne, wertAriaLabel, zahl, zeitLabel,
  type CrmFilter, type KpiKachel, type KpiWert,
} from './crm-kpi.js';

export type KpiZustand = { kind: 'laden' } | { kind: 'fehler'; text: string | null } | { kind: 'ok'; daten: CrmKpi };

/**
 * #839 KPI-Leiste über der Kundenliste: fünf Zähl-Kacheln (Haupt- und Teilwert jeweils ein Link auf `/crm?<filter>`,
 * teilbar) + Kachel „Letzter Abgleich“ (Information, nicht klickbar; Ampel mit Textlabel, nicht nur Farbe).
 */
export function KpiLeiste({ zustand, filter, onErneut }: { zustand: KpiZustand; filter: CrmFilter; onErneut: () => void }) {
  if (zustand.kind === 'fehler') {
    return (
      <div className="pb-card crm-hinweis crm-hinweis--fehler crm-kpi-fehler" role="alert">
        <p>{zustand.text ?? 'Die Kennzahlen konnten nicht geladen werden.'}</p>
        <button type="button" className="pb-button pb-button--quiet" onClick={onErneut}>
          Kennzahlen erneut laden
        </button>
      </div>
    );
  }
  if (zustand.kind === 'laden') {
    return (
      <ul className="crm-kpi" aria-busy="true" aria-label="Kennzahlen werden geladen">
        {Array.from({ length: 6 }, (_, i) => (
          <li key={i} className="pb-card crm-kpi-kachel crm-kpi-kachel--laden" aria-hidden="true">
            <span className="crm-kpi-titel">&nbsp;</span>
            <span className="crm-kpi-zahl">–</span>
          </li>
        ))}
      </ul>
    );
  }
  const k = zustand.daten;
  return (
    <ul className="crm-kpi" aria-label="Kennzahlen Kundenstamm">
      {kacheln(k).map((kachel) => (
        <li key={kachel.id} className="pb-card crm-kpi-kachel">
          <h2 className="crm-kpi-titel">{kachel.titel}</h2>
          <WertLink kachel={kachel} wert={kachel.haupt} filter={filter} haupt />
          <WertLink kachel={kachel} wert={kachel.teil} filter={filter} />
          {kachel.hinweis && (
            <p className="crm-kpi-zeile crm-kpi-hinweis crm-muted" title={kachel.hinweis} aria-hidden="true">
              {kachel.hinweis}
            </p>
          )}
        </li>
      ))}
      <li className="pb-card crm-kpi-kachel crm-kpi-kachel--abgleich">
        <h2 className="crm-kpi-titel">Letzter Abgleich</h2>
        {k.letzter_abgleich ? (
          <>
            <p className={`crm-ampel crm-ampel--${k.letzter_abgleich.ampel}`}>
              <span className="crm-ampel-punkt" aria-hidden="true" />
              <span>{AMPEL_LABEL[k.letzter_abgleich.ampel]}</span>
            </p>
            <p className="crm-kpi-zeile">
              <time dateTime={k.letzter_abgleich.gestartetAt}>{zeitLabel(k.letzter_abgleich.gestartetAt)}</time>
            </p>
            <p className="crm-kpi-zeile crm-muted">
              {modusLabel(k.letzter_abgleich.modus)} · {laufStatusLabel(k.letzter_abgleich.status)}
            </p>
            <p className="crm-kpi-zeile crm-muted">
              <span className="pb-num">{zahl(k.letzter_abgleich.neu)}</span> neu · <span className="pb-num">{zahl(k.letzter_abgleich.aktualisiert)}</span> aktualisiert
            </p>
          </>
        ) : (
          <>
            <p className="crm-ampel crm-ampel--rot">
              <span className="crm-ampel-punkt" aria-hidden="true" />
              <span>Noch kein Abgleich</span>
            </p>
            <p className="crm-kpi-zeile crm-muted">Shopify-Import noch nicht gelaufen.</p>
          </>
        )}
      </li>
    </ul>
  );
}

function WertLink({ kachel, wert, filter, haupt = false }: { kachel: KpiKachel; wert: KpiWert; filter: CrmFilter; haupt?: boolean }) {
  const pfad = listenPfad(wert.filter);
  const aktiv = istAktiv(wert, filter);
  return (
    <a
      href={pfad}
      onClick={onLinkClick(pfad)}
      className={`crm-kpi-wert${haupt ? ' crm-kpi-wert--haupt' : ''}${aktiv ? ' crm-kpi-wert--aktiv' : ''}`}
      aria-label={wertAriaLabel(kachel, wert)}
      aria-current={aktiv ? 'true' : undefined}
    >
      <span className={haupt ? 'crm-kpi-zahl pb-num' : 'crm-kpi-teilzahl pb-num'}>{zahl(wert.wert)}</span>
      <span className="crm-kpi-label">{wert.label}</span>
    </a>
  );
}

/** Aktive Filter als entfernbare Chips („× Blacklist“) + „Alle Filter entfernen“ */
export function FilterChips({ filter }: { filter: CrmFilter }) {
  const aktiv = aktiveFilter(filter);
  if (aktiv.length === 0) return null;
  return (
    <div className="crm-chips" role="group" aria-label="Aktive Filter">
      <ul>
        {aktiv.map((k) => {
          const label = filterLabel(k, filter[k]!);
          const pfad = listenPfad(ohne(filter, k));
          return (
            <li key={k}>
              <a className="crm-chip" href={pfad} onClick={onLinkClick(pfad)} aria-label={`Filter ${label} entfernen`}>
                <span aria-hidden="true">×</span> {label}
              </a>
            </li>
          );
        })}
      </ul>
      <a className="crm-chips-alle" href={listenPfad({})} onClick={onLinkClick(listenPfad({}))}>
        Alle Filter entfernen
      </a>
    </div>
  );
}
